import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { FieldWrapper, FormlyModule } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { canMoveLayoutNode, listMoveToDestinations, type FormOverlayFieldNode } from '@phoenix/common';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { BaseDetailComponent } from '../../../common/base-detail.component';
import { IBaseModel } from '../../../generated/generated-types';
import { layoutNodeIdFromFormlyField } from './layout-node-id-from-formly';

@Component({
    selector: 'formly-layout-edit-node-wrapper',
    templateUrl: './layout-edit-node-wrapper.component.html',
    styleUrls: ['./layout-edit-node-wrapper.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormlyModule, ButtonModule, TooltipModule, TranslatePipe],
})
export class LayoutEditNodeWrapperComponent extends FieldWrapper {
    readonly layoutNodeId = computed(() => layoutNodeIdFromFormlyField(this.field));

    readonly isSelected = computed(() => {
        const host = this.layoutHost();
        return !!host && host.selectedLayoutNodeId() === this.layoutNodeId();
    });

    readonly isOverlayHidden = computed(() => {
        return (this.field as FormOverlayFieldNode | undefined)?.overlayHidden === true;
    });

    readonly treeLocked = computed(() => this.layoutHost()?.isLayoutTreeLocked() === true);

    /**
     * Formly mutates `fieldGroup` in place (nested forms start `[]` then fill).
     * Do not `computed()` this — that caches the first empty read forever.
     */
    isLeaf(): boolean {
        return !Array.isArray(this.field?.fieldGroup);
    }

    isStructureNode(): boolean {
        const field = this.field;
        if (!field || this.isLeaf()) {
            return false;
        }
        if (field.type === 'tabBootstrap' || field.parent?.type === 'tabBootstrap') {
            return true;
        }
        const key = field.key;
        return key == null || key === '';
    }

    isEmptyGroup(): boolean {
        const group = this.field?.fieldGroup;
        return Array.isArray(group) && group.length === 0;
    }

    readonly canMoveUp = computed(() => this.canMove('up'));
    readonly canMoveDown = computed(() => this.canMove('down'));
    readonly canMoveTo = computed(() => this.hasMoveDestinations());

    onSelect(event: Event): void {
        event.stopPropagation();
        this.layoutHost()?.selectLayoutNode(this.layoutNodeId());
    }

    onMoveUp(event?: Event): void {
        event?.stopPropagation();
        this.layoutHost()?.moveSelectedLayoutNode('up');
    }

    onMoveDown(event?: Event): void {
        event?.stopPropagation();
        this.layoutHost()?.moveSelectedLayoutNode('down');
    }

    onMoveTo(event?: Event): void {
        event?.stopPropagation();
        void this.layoutHost()?.openLayoutMoveToDialog();
    }

    onToggleOverlayHide(event?: Event): void {
        event?.stopPropagation();
        this.layoutHost()?.toggleSelectedOverlayHidden();
    }

    onRemove(event?: Event): void {
        event?.stopPropagation();
        this.layoutHost()?.removeSelectedLayoutNode();
    }

    onContextMenu(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        const host = this.layoutHost();
        host?.selectLayoutNode(this.layoutNodeId());
        host?.openLayoutContextMenu(event);
    }

    private canMove(direction: 'up' | 'down'): boolean {
        const host = this.layoutHost();
        if (!host?.fields) {
            return false;
        }
        return canMoveLayoutNode({
            tree: host.fields as FormOverlayFieldNode[],
            nodeId: this.layoutNodeId(),
            direction,
        });
    }

    private hasMoveDestinations(): boolean {
        const host = this.layoutHost();
        if (!host?.fields) {
            return false;
        }
        return listMoveToDestinations({
            tree: host.fields as FormOverlayFieldNode[],
            nodeId: this.layoutNodeId(),
        }).length > 0;
    }

    private layoutHost(): BaseDetailComponent<IBaseModel> | undefined {
        return this.field?.options?.formState?.detailComponent as BaseDetailComponent<IBaseModel> | undefined;
    }
}
